import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Bitcoin, Copy, Loader2, CheckCircle2, Clock, ShieldCheck, ExternalLink, Percent, Smartphone } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { BTC_ENABLED } from "@/lib/flags";

export default function Btc() {
  const { user } = useApp();
  const isAdmin = user?.role === "admin";
  const [method, setMethod] = useState(BTC_ENABLED ? "btc" : "upi"); // btc | upi
  const [info, setInfo] = useState(null);
  const [upi, setUpi] = useState(null);
  const [quote, setQuote] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [f, setF] = useState({ tx_id: "", amount_btc: "", credits_requested: 500, note: "" });
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [subs, setSubs] = useState([]);
  const [grant, setGrant] = useState({});

  const loadMine = async () => { try { const { data } = await api.get("/btc/mine"); setMine(data.submissions || []); } catch (_) {} };
  const loadSubs = async () => { try { const { data } = await api.get("/btc/submissions"); setSubs(data.submissions || []); } catch (_) {} };

  useEffect(() => {
    api.get("/btc/info").then(({ data }) => setInfo(data)).catch(() => {});
    api.get("/upi/info").then(({ data }) => setUpi(data)).catch(() => {});
    api.get(`/pricing?currency=INR`).then(({ data }) => setPricing(data)).catch(() => {});
    if (user) loadMine(); if (user?.role === "admin") loadSubs(); /* eslint-disable-next-line */
  }, [user]);

  const payRef = React.useRef(null);
  const [params] = useSearchParams();
  useEffect(() => {
    const c = parseInt(params.get("credits") || "", 10);
    const m = params.get("method");
    if (m === "upi" || (m === "btc" && BTC_ENABLED)) setMethod(m);
    if (c && c > 0) {
      setF((prev) => ({ ...prev, credits_requested: c }));
      setTimeout(() => payRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
    /* eslint-disable-next-line */
  }, []);
  const choosePack = (credits, m) => {
    setMethod(m);
    setF((prev) => ({ ...prev, credits_requested: credits }));
    setTimeout(() => payRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  // Live quote whenever credits or method change
  useEffect(() => {
    const credits = parseInt(f.credits_requested || 0, 10);
    if (!credits || credits < 1) { setQuote(null); return; }
    let active = true;
    const t = setTimeout(() => {
      const ep = method === "upi" ? `/upi/quote?credits=${credits}` : `/btc/quote?credits=${credits}`;
      api.get(ep).then(({ data }) => {
        if (!active) return;
        setQuote(data);
        if (method === "btc") setF((prev) => ({ ...prev, amount_btc: String(data.btc_amount) }));
      }).catch(() => {});
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [f.credits_requested, method]);

  const submit = async () => {
    if (!user) { toast.error("Sign in first."); return; }
    if (!f.tx_id.trim()) { toast.error(method === "upi" ? "Enter your UPI reference / UTR." : "Enter your Transaction ID."); return; }
    setBusy(true);
    try {
      await api.post("/btc/submit", {
        tx_id: f.tx_id, amount_btc: f.amount_btc, credits_requested: f.credits_requested, note: f.note, method,
      });
      toast.success("Submitted! We'll verify and grant your credits shortly.");
      setF({ ...f, tx_id: "", note: "" }); loadMine();
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doGrant = async (id) => {
    const credits = parseInt(grant[id] || subs.find((s) => s.id === id)?.credits_requested || 0, 10);
    try { await api.post(`/btc/grant/${id}`, { credits }); toast.success(`Granted ${credits} credits`); loadSubs(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const discount = info?.discount_pct ?? 3;
  const inrFmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);
  const isBtc = method === "btc";

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand">{BTC_ENABLED ? <Bitcoin size={14} /> : <Smartphone size={14} />} Add credits</span>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter mt-3">{BTC_ENABLED ? "Top up with Bitcoin or UPI" : "Top up with UPI"}</h1>
        <p className="text-sec mt-2 text-sm">Pay, then submit your reference. We verify and credit your account manually (usually within a few hours).</p>
      </motion.div>

      {/* Credit packs */}
      {pricing && (
        <div className="mt-8" data-testid="credit-packs">
          <div className="text-xs uppercase tracking-widest text-sec mb-3">{BTC_ENABLED ? `Pick a credit pack — pay by Bitcoin (${discount}% off) or UPI` : "Pick a credit pack — pay by UPI"}</div>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {pricing.packs.map((p) => (
              <div key={p.credits} className="rud-card rounded-2xl p-4 flex flex-col" data-testid={`pack-${p.credits}`}>
                <div className="font-serif text-3xl tracking-tight">{p.credits}</div>
                <div className="text-xs text-sec">credits</div>
                <div className="mt-2 text-sm font-medium">${p.usd}</div>
                <div className="text-xs text-sec">{pricing.symbol}{new Intl.NumberFormat("en-IN").format(p.local)}</div>
                {BTC_ENABLED && <div className="text-[11px] text-brand mt-1">BTC: save {discount}%</div>}
                <div className="mt-3 space-y-1.5">
                  {BTC_ENABLED && (
                    <button data-testid={`pack-btc-${p.credits}`} onClick={() => choosePack(p.credits, "btc")} className="btn-primary w-full h-8 rounded-full text-xs flex items-center justify-center gap-1.5"><Bitcoin size={13} /> Buy with BTC</button>
                  )}
                  <button data-testid={`pack-upi-${p.credits}`} onClick={() => choosePack(p.credits, "upi")} className="btn-outline w-full h-8 rounded-full text-xs flex items-center justify-center gap-1.5"><Smartphone size={13} /> Buy with UPI</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Method toggle */}
      <div ref={payRef} className={`mt-6 ${BTC_ENABLED ? "inline-flex" : "hidden"} rounded-full border border-white/10 bg-white/5 p-1`} data-testid="pay-method-toggle">
        <button data-testid="method-btc" onClick={() => setMethod("btc")} className={`px-4 h-9 rounded-full text-sm flex items-center gap-2 transition-colors ${isBtc ? "bg-brand text-black font-medium" : "text-sec hover:text-brand"}`}>
          <Bitcoin size={15} /> Bitcoin <span className="text-xs opacity-80">· {discount}% off</span>
        </button>
        <button data-testid="method-upi" onClick={() => setMethod("upi")} className={`px-4 h-9 rounded-full text-sm flex items-center gap-2 transition-colors ${!isBtc ? "bg-brand text-black font-medium" : "text-sec hover:text-brand"}`}>
          <Smartphone size={15} /> UPI / GPay
        </button>
      </div>

      {/* Discount / info banner */}
      {isBtc ? (
        <motion.div key="btc-banner" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex items-center gap-3 rounded-2xl px-5 py-4 border border-brand/30 bg-brand/10" data-testid="btc-discount-banner">
          <div className="h-9 w-9 rounded-full bg-brand/20 flex items-center justify-center shrink-0"><Percent size={16} className="text-brand" /></div>
          <div>
            <div className="text-sm font-semibold" data-testid="btc-discount-text">Pay in Bitcoin & save {discount}%</div>
            <div className="text-xs text-sec">The {discount}% discount is applied automatically to your BTC quote below.</div>
          </div>
        </motion.div>
      ) : (
        <motion.div key="upi-banner" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex items-center gap-3 rounded-2xl px-5 py-4 border border-white/10 bg-white/5" data-testid="upi-banner">
          <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center shrink-0"><Smartphone size={16} className="text-brand" /></div>
          <div>
            <div className="text-sm font-semibold">Pay via GPay / PhonePe / Paytm</div>
            <div className="text-xs text-sec">Scan the QR or use the UPI ID, then submit your UPI reference / UTR.</div>
          </div>
        </motion.div>
      )}

      <div className="grid md:grid-cols-2 gap-5 mt-6">
        {/* Wallet / UPI + QR */}
        {isBtc ? (
          <div className="rud-card rounded-2xl p-6 text-center" data-testid="btc-wallet">
            {info?.qr_url
              ? <img src={info.qr_url} alt="BTC QR" className="mx-auto rounded-lg bg-white p-2" width={200} height={200} data-testid="btc-qr" />
              : <div className="mx-auto h-[200px] w-[200px] rounded-lg bg-white/5 flex items-center justify-center text-xs text-sec">QR unavailable</div>}
            <div className="text-xs text-sec mt-4">Wallet address (BTC)</div>
            <div className="flex items-center gap-2 justify-center mt-1">
              <code className="text-xs break-all" data-testid="btc-address">{info?.wallet_address || "…"}</code>
              <button onClick={() => { navigator.clipboard.writeText(info?.wallet_address || ""); toast.success("Address copied"); }} className="text-sec hover:text-brand shrink-0" data-testid="btc-copy"><Copy size={14} /></button>
            </div>
            {info?.trust_wallet_url && (
              <a href={info.trust_wallet_url} target="_blank" rel="noreferrer" data-testid="btc-trustwallet"
                className="btn-outline mt-4 inline-flex items-center justify-center gap-2 rounded-full h-9 px-4 text-xs w-full">
                Pay via Trust Wallet <ExternalLink size={13} />
              </a>
            )}
          </div>
        ) : (
          <div className="rud-card rounded-2xl p-6 text-center" data-testid="upi-wallet">
            {(quote?.qr_url || upi?.qr_url)
              ? <img src={quote?.qr_url || upi?.qr_url} alt="UPI QR" className="mx-auto rounded-lg bg-white p-2" width={200} height={200} data-testid="upi-qr" />
              : <div className="mx-auto h-[200px] w-[200px] rounded-lg bg-white/5 flex items-center justify-center text-xs text-sec">QR unavailable</div>}
            <div className="text-xs text-sec mt-4">UPI ID — {upi?.payee_name || ""}</div>
            <div className="flex items-center gap-2 justify-center mt-1">
              <code className="text-xs break-all" data-testid="upi-id">{upi?.upi_id || "…"}</code>
              <button onClick={() => { navigator.clipboard.writeText(upi?.upi_id || ""); toast.success("UPI ID copied"); }} className="text-sec hover:text-brand shrink-0" data-testid="upi-copy"><Copy size={14} /></button>
            </div>
            {upi?.upi_uri && (
              <a href={quote?.upi_uri || upi.upi_uri} data-testid="upi-openapp"
                className="btn-outline mt-4 inline-flex items-center justify-center gap-2 rounded-full h-9 px-4 text-xs w-full">
                Open UPI app <ExternalLink size={13} />
              </a>
            )}
          </div>
        )}

        {/* Quote + submit */}
        <div className="rud-card rounded-2xl p-6">
          <div className="text-sm font-medium mb-3">Buy credits</div>
          <label className="text-xs text-sec">Credits you want</label>
          <input data-testid="pay-credits" type="number" min={1} value={f.credits_requested} onChange={(e) => setF({ ...f, credits_requested: e.target.value })} placeholder="Credits wanted" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-1" />

          {quote && (
            <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3 text-sm space-y-1" data-testid="pay-quote">
              <div className="flex justify-between text-sec"><span>Price</span><span>₹{inrFmt(quote.inr)}</span></div>
              {isBtc && <div className="flex justify-between text-brand"><span>{discount}% BTC discount</span><span data-testid="btc-quote-save">− ₹{inrFmt(quote.you_save_inr)}</span></div>}
              <div className="flex justify-between font-medium border-t border-white/10 pt-1 mt-1"><span>You pay</span><span data-testid="pay-quote-inr">₹{inrFmt(isBtc ? quote.inr_after_discount : quote.inr)}</span></div>
              {isBtc && <div className="flex justify-between font-semibold text-brand"><span>≈ Send</span><span data-testid="btc-quote-btc">{quote.btc_amount} BTC</span></div>}
            </div>
          )}

          <div className="text-xs text-sec mt-4 mb-1">After paying, submit your transaction:</div>
          <input data-testid="pay-txid" value={f.tx_id} onChange={(e) => setF({ ...f, tx_id: e.target.value })} placeholder={isBtc ? "Bitcoin Transaction ID (TXID)" : "UPI reference / UTR number"} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          {isBtc && <input data-testid="btc-amount" value={f.amount_btc} onChange={(e) => setF({ ...f, amount_btc: e.target.value })} placeholder="Amount sent (BTC)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-2" />}
          <button data-testid="pay-submit" onClick={submit} disabled={busy} className="btn-primary w-full h-11 mt-3 flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Submit for verification</button>
        </div>
      </div>

      {user && mine.length > 0 && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-sec mb-2">Your submissions</div>
          {mine.map((s) => (
            <div key={s.id} className="flex items-center justify-between rud-card rounded-lg px-4 py-2.5 mb-2 text-sm">
              <span className="truncate">{(s.method || "btc").toUpperCase()} · {s.tx_id}</span>
              <span className={`text-xs flex items-center gap-1 ${s.status === "granted" ? "text-green-500" : "text-sec"}`}>{s.status === "granted" ? <><CheckCircle2 size={13} /> {s.granted_credits} credits</> : <><Clock size={13} /> pending</>}</span>
            </div>
          ))}
        </div>
      )}

      {isAdmin && (
        <div className="mt-10" data-testid="btc-admin">
          <h2 className="font-serif text-2xl mb-3">Admin — payment submissions</h2>
          {subs.length === 0 && <p className="text-sec text-sm">No submissions yet.</p>}
          {subs.map((s) => (
            <div key={s.id} className="rud-card rounded-xl p-4 mb-2 flex flex-wrap items-center gap-3 justify-between" data-testid={`btc-sub-${s.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium">{s.email} <span className="text-xs text-brand ml-1">{(s.method || "btc").toUpperCase()}</span></div>
                <div className="text-xs text-sec break-all">Ref: {s.tx_id} · {s.amount_btc || "?"} · wants {s.credits_requested}</div>
              </div>
              {s.status === "granted" ? <span className="text-green-500 text-sm flex items-center gap-1"><CheckCircle2 size={14} /> {s.granted_credits}</span> : (
                <div className="flex items-center gap-2">
                  <input type="number" defaultValue={s.credits_requested} onChange={(e) => setGrant({ ...grant, [s.id]: e.target.value })} className="rud-input w-24 rounded-lg px-2 py-1.5 text-sm" />
                  <button data-testid={`btc-grant-${s.id}`} onClick={() => doGrant(s.id)} className="btn-primary rounded-full h-9 px-4 text-sm">Grant credits</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
